import { useEffect, useMemo, useState } from 'react';
import { isEqual, omit } from 'lodash-es';

import { Control } from 'react-hook-form';

import { CoreBox, CoreTooltip } from '@youscience/core';

import ActionButton from '@components/ActionButton';

import useSaveSchoolFilterStore from '@store/SaveSchoolFilterStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';
import { SchoolSearchState } from '@store/SchoolSearchFiltersStore';

import { CollegeSearchFormValues } from '@pages/SchoolList/SchoolList';

import { FilterAccordion } from '@components/FilterAccordion';
import { RadioButtonGroup } from '@components/Form/RadioButtonGroup';

import DeleteFilterIcon from '@images/delete-filter.svg?react';
import { SCHOOL_FILTERS_DATA } from '@constants/fullSchoolsList';

import { sxStyles } from './SaveFilter.styles';

const MAX_NUMBER_OF_SAVED_FILTERS = 10;

interface SaveFilterProps {
  control: Control<CollegeSearchFormValues, unknown>;
  filters: SchoolSearchState;
  isExpanded?: boolean;
  handleRadioChange: (filters: SchoolSearchState) => void;
}

export const SaveFilter = (props: SaveFilterProps) => {
  const { control, filters, isExpanded, handleRadioChange } = props;

  const { userPreferences } = useUserPreferencesStore((state) => state);
  const { saveNewFilter, deleteSavedFilter } = useSaveSchoolFilterStore((state) => state);

  const [isFiltersExistToSave, setIsFiltersExistToSave] = useState(false);
  const [isSaveFilterButtonDisabled, setIsSaveFilterButtonDisabled] = useState(false);
  const [isMaxSavedFilters, setIsMaxSavedFilters] = useState(false);
  const [saveFilterTooltipMessage, setSaveFilterTooltipMessage] = useState('Save Filter');
  const [isSavedFilterSelected, setIsSavedFilterSelected] = useState(false);

  const omittedSelectedFilters = useMemo(() => omit(filters, ['skip', 'limit']), [filters]);

  const savedFilters = userPreferences.filters?.colleges ?? [];

  useEffect(() => {
    setIsMaxSavedFilters(savedFilters.length >= MAX_NUMBER_OF_SAVED_FILTERS);
  }, [savedFilters, MAX_NUMBER_OF_SAVED_FILTERS]);

  useEffect(() => {
    setIsSaveFilterButtonDisabled(isMaxSavedFilters || !isFiltersExistToSave || isSavedFilterSelected);

    let message = 'Save Filter';

    if (isMaxSavedFilters) {
      message = 'The maximum number of saved filters is 10.';
    } else if (!isFiltersExistToSave) {
      message = 'Select filters to save';
    }

    setSaveFilterTooltipMessage(message);
  }, [isMaxSavedFilters, isFiltersExistToSave, isSavedFilterSelected]);

  useEffect(() => {
    const filtersToSave = Object.keys(omittedSelectedFilters);

    setIsFiltersExistToSave(filtersToSave.length > 0);

    setIsSavedFilterSelected(
      savedFilters.some((filterItem) => isEqual(omit(filterItem, 'id', 'createdDate'), omittedSelectedFilters)),
    );
  }, [filters]);

  const savedFilterOptions = useMemo(
    () =>
      savedFilters?.map((filterItem) => {
        const formattedData = new Date(filterItem.createdDate).toLocaleDateString('en-US');
        const formattedTime = new Date(filterItem.createdDate).toLocaleTimeString([], { timeStyle: 'short' });

        return {
          id: filterItem?.id?.toString(),
          label: `${formattedData} ${formattedTime}`,
          value: filterItem.createdDate.toString(),
        };
      }),
    [savedFilters],
  );

  const handleSaveFilter = () => {
    if (isSaveFilterButtonDisabled || isSavedFilterSelected) return;

    void saveNewFilter(omittedSelectedFilters);

    setIsSavedFilterSelected(true);
  };

  const handleDeleteSavedFilter = (filterId: string) => {
    const preparedSavedFilterData = omit(
      savedFilters.find((filterItem) => filterItem?.id?.toString() === filterId),
      'id',
      'createdDate',
    );

    if (isEqual(preparedSavedFilterData, omittedSelectedFilters)) {
      setIsSavedFilterSelected(false);
    }

    deleteSavedFilter(filterId);
  };

  const handleOnRadioClick = (filterId: string) => {
    const preparedFilterData = omit(
      savedFilters.find((filterItem) => filterItem?.id?.toString() === filterId),
      'id',
      'createdDate',
    );

    handleRadioChange(preparedFilterData);
  };

  return (
    <>
      <CoreTooltip title={saveFilterTooltipMessage}>
        <CoreBox data-testid="saveFilter" sx={sxStyles.saveButtonWrap}>
          <ActionButton disabled={isSaveFilterButtonDisabled} onClick={handleSaveFilter}>
            Save Filter
          </ActionButton>
        </CoreBox>
      </CoreTooltip>

      {savedFilters.length > 0 && (
        <FilterAccordion
          isExpanded={isExpanded}
          accordionTitle={SCHOOL_FILTERS_DATA.savedFilters.title}
          data-testid="savedFilters"
        >
          <RadioButtonGroup
            name={SCHOOL_FILTERS_DATA.savedFilters.fieldName}
            options={savedFilterOptions}
            control={control}
            endAdornment={
              <CoreBox sx={sxStyles.radioEndAdornment}>
                <DeleteFilterIcon data-testid="deleteFilterIcon" />
              </CoreBox>
            }
            onEndAdornmentClick={handleDeleteSavedFilter}
            onRadioClick={handleOnRadioClick}
          />
        </FilterAccordion>
      )}
    </>
  );
};
